import { useState } from "react";
import axios from "axios";
import { API, getAuthHeaders, useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Search, User, Mail, Phone, Calendar, IdCard, ShieldCheck,
  CreditCard, Wallet, Activity, AlertTriangle, Crown, Star, Users,
  CheckCircle2, XCircle, ShieldOff, ArrowUpCircle, ArrowDownCircle
} from "lucide-react";

const TIER_META = {
  affiliate: { label: "Affiliate", icon: Users, color: "text-zinc-300", bg: "bg-zinc-500/10", border: "border-zinc-500/30" },
  sub_distributor: { label: "Sub-Distributor", icon: Star, color: "text-sky-300", bg: "bg-sky-500/10", border: "border-sky-500/30" },
  main_distributor: { label: "Main Distributor", icon: Crown, color: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/30" },
};

export default function UserLookup() {
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const search = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (!query.trim()) return;
    setLoading(true);
    setData(null);
    try {
      const r = await axios.get(
        `${API}/admin/users/lookup`,
        { params: { q: query.trim() }, headers: getAuthHeaders() }
      );
      setData(r.data);
    } catch (err) {
      const d = err.response?.data?.detail || "Lookup failed";
      setError(typeof d === "string" ? d : "Lookup failed");
      toast.error(typeof d === "string" ? d : "Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  // Re-run the most recent search after a mutation (cap, rank, MT5 action)
  const refresh = async () => {
    if (!query.trim()) return;
    try {
      const r = await axios.get(
        `${API}/admin/users/lookup`,
        { params: { q: query.trim() }, headers: getAuthHeaders() }
      );
      setData(r.data);
    } catch (err) {
      /* keep stale data; user can press Search again */
    }
  };

  return (
    <div className="space-y-6" data-testid="user-lookup-section">
      <div className="p-5 rounded-xl border border-white/10 bg-[#13131A]/60">
        <h3 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
          <Search size={14} className="text-[#7DD3FC]" />
          User Lookup
        </h3>
        <p className="text-[11px] text-zinc-500 mb-4">
          Search by Display ID (VAT-XXXXXX), email, user_id, or referral code.
        </p>
        <form onSubmit={search} className="flex gap-2">
          <input
            data-testid="user-lookup-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="VAT-000021 · alice@x.com · FLUXABC123"
            className="flex-1 px-3 py-2 rounded-md bg-zinc-950 border border-zinc-800 text-white text-sm font-mono outline-none focus:border-[#7DD3FC]/50"
          />
          <button
            type="submit"
            data-testid="user-lookup-submit"
            disabled={loading || !query.trim()}
            className="px-4 py-2 rounded-md bg-[#7DD3FC] hover:bg-[#BAE6FD] text-[#0A0A0C] text-xs font-semibold disabled:opacity-40 transition-colors"
          >
            {loading ? "Searching…" : "Search"}
          </button>
        </form>
        {error && <p className="text-[11px] text-rose-400 mt-2" data-testid="user-lookup-error">{error}</p>}
      </div>

      {data && <UserProfile data={data} onChanged={refresh} />}
    </div>
  );
}

function UserProfile({ data, onChanged }) {
  const u = data.user;
  const meta = TIER_META[u.rank?.tier] || TIER_META.affiliate;
  const Icon = meta.icon;
  const { user: authUser } = useAuth();
  const isMaster = authUser?.role === "master_admin";

  return (
    <div className="space-y-4" data-testid="user-lookup-result">
      {/* Header card */}
      <div className={`p-5 rounded-xl border ${meta.border} ${meta.bg}`}>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl ${meta.bg} border ${meta.border} flex items-center justify-center`}>
              <Icon size={20} className={meta.color} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-bold text-white">{u.name || "—"}</h3>
                <span className="text-[10px] tracking-[0.25em] uppercase font-mono text-[#7DD3FC]">{u.display_id || u.user_id}</span>
              </div>
              <p className="text-[11px] text-zinc-400 flex items-center gap-1.5 mt-0.5">
                <Mail size={10} /> {u.email}
                {u.phone && <><span className="text-zinc-700">·</span><Phone size={10} /> {u.phone}</>}
              </p>
              <p className="text-[10px] text-zinc-500 flex items-center gap-1.5 mt-0.5">
                <Calendar size={10} /> Joined {u.created_at?.slice(0, 10) || "—"}
                <span className="text-zinc-700">·</span>
                Code <span className="font-mono text-[#7DD3FC]">{u.referral_code}</span>
                {u.signup_method && (
                  <>
                    <span className="text-zinc-700">·</span>
                    <span
                      data-testid="user-signup-method"
                      className={`px-1.5 py-0.5 rounded-md text-[9px] font-semibold uppercase tracking-wider ${
                        u.signup_method === "direct"
                          ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30"
                          : u.signup_method === "auto_pull"
                          ? "bg-sky-500/10 text-sky-300 border border-sky-500/30"
                          : "bg-zinc-500/10 text-zinc-300 border border-zinc-500/30"
                      }`}
                      title={`Referral source: ${u.signup_method}${u.signup_referral_code_used ? ` · code ${u.signup_referral_code_used}` : ""}`}
                    >
                      {u.signup_method.replace("_", " ")}
                    </span>
                  </>
                )}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${meta.bg} border ${meta.border} text-[10px] font-semibold ${meta.color}`}>
              {meta.label} · {u.direct_referrals} ref
              {u.rank?.manual && (
                <span data-testid="user-rank-manual-badge" className="ml-1 px-1 rounded bg-amber-500/20 text-amber-200 text-[8px] font-bold tracking-wider">PINNED</span>
              )}
            </span>
            {u.income_cap && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/10 border border-rose-500/30 text-[10px] font-semibold text-rose-300" data-testid="user-lookup-income-cap">
                <AlertTriangle size={10} /> {u.income_cap.max_referral_bonus_pct}% cap · {u.income_cap.reason}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Master-only admin actions: income cap override + manual rank */}
      {isMaster && (
        <AdminActionsPanel u={u} onChanged={onChanged} />
      )}

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Direct Earnings" value={`₹${(u.direct_earnings || 0).toLocaleString()}`} icon={Wallet} color="text-emerald-400" />
        <StatTile label="Team Locked" value={`₹${(u.team_bonus_locked || 0).toLocaleString()}`} icon={Wallet} color="text-zinc-300" />
        <StatTile label="KYC" value={u.kyc_status || "pending"} icon={ShieldCheck}
          color={u.kyc_status === "verified" ? "text-emerald-400" : u.kyc_status === "rejected" ? "text-rose-400" : "text-amber-400"} />
        <StatTile label="2FA" value={u.two_fa_enabled ? "Enabled" : "Off"} icon={Activity} color={u.two_fa_enabled ? "text-emerald-400" : "text-zinc-500"} />
      </div>

      {/* KYC personal details */}
      {u.kyc_personal && (
        <Section title="KYC Personal Information" icon={IdCard} testid="user-kyc-personal">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
            <KV label="Education" value={u.kyc_personal.education} />
            <KV label="Occupation" value={u.kyc_personal.occupation} />
            <KV label="Father" value={u.kyc_personal.father_name} />
            <KV label="Mother" value={u.kyc_personal.mother_name} />
            <KV label="Siblings" value={u.kyc_personal.has_no_siblings ? "None" : (u.kyc_personal.sibling_names || []).join(", ") || "—"} />
            <KV label="Submitted" value={u.kyc_personal.submitted_at?.slice(0, 10)} />
            {u.kyc_personal.aadhaar && (
              <>
                <KV label="Aadhaar" value={u.kyc_personal.aadhaar.masked_aadhaar} />
                <KV label="Address" value={u.kyc_personal.aadhaar.address?.full || "—"} col2 />
              </>
            )}
          </div>
          {u.kyc_rejection_reason && (
            <div className="mt-3 p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-[11px] text-rose-300" data-testid="user-kyc-rejection">
              Rejection: {u.kyc_rejection_reason}
            </div>
          )}
        </Section>
      )}

      {/* Bank */}
      <Section title="Bank Details" icon={CreditCard} testid="user-bank">
        {u.bank_details && (u.bank_details.account_number_masked || u.bank_details.bank_name) ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <KV label="Holder" value={u.bank_details.account_holder || u.bank_details.name} />
            <KV label="Account" value={u.bank_details.account_number_masked || "—"} mono />
            <KV label="IFSC" value={u.bank_details.ifsc} mono />
            <KV label="Bank" value={u.bank_details.bank_name} />
          </div>
        ) : (
          <p className="text-[11px] text-zinc-500">No bank linked.</p>
        )}
      </Section>

      {/* MT5 accounts */}
      <Section title="MT5 Trading Accounts" icon={Activity} testid="user-mt5">
        {!data.mt5_primary?.cent && !data.mt5_primary?.usd && (!data.mt5_extras || data.mt5_extras.length === 0) && (
          <p className="text-[11px] text-zinc-500">No MT5 accounts linked.</p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {["cent", "usd"].map((t) => {
            const a = data.mt5_primary?.[t];
            if (!a) return null;
            return (
              <div key={t} data-testid={`user-mt5-primary-${t}`} className="p-3 rounded-lg border border-white/10 bg-zinc-950/60">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#7DD3FC] font-semibold">PRIMARY · {t.toUpperCase()}</span>
                  <StatusPill status={a.status} />
                </div>
                <div className="text-[11px] text-zinc-300 font-mono">#{a.mt5_login_id} · {a.broker_name}</div>
                <div className="text-[10px] text-zinc-500">{a.mt5_server} · Plan {a.plan?.toUpperCase()} · Expires {a.expires_at?.slice(0, 10) || "—"}</div>
                <MT5ActionButtons userId={u.user_id} accountType={t} account={a} onChanged={onChanged} />
              </div>
            );
          })}
          {(data.mt5_extras || []).map((e) => (
            <div key={e.id} data-testid={`user-mt5-extra-${e.id}`} className="p-3 rounded-lg border border-zinc-800/60 bg-zinc-950/40">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 font-semibold">EXTRA · {e.account_type?.toUpperCase()}</span>
                <span className="text-[10px] text-zinc-500">{e.linked_at?.slice(0, 10)}</span>
              </div>
              <div className="text-[11px] text-zinc-300 font-mono">{e.nickname}</div>
              <div className="text-[10px] text-zinc-500">#{e.mt5_login_id} · {e.broker_name} · {e.mt5_server}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Payments */}
      <Section title="Payments" icon={Wallet} testid="user-payments">
        <div className="space-y-2">
          {data.payments?.signup_joining_fee && (
            <div className="p-2.5 rounded-lg border border-emerald-500/20 bg-emerald-500/5 text-[11px] flex items-center justify-between">
              <span className="text-emerald-300">₹{data.payments.signup_joining_fee.amount_inr} platform joining fee</span>
              <span className="text-zinc-500 font-mono text-[10px]">{data.payments.signup_joining_fee.paid_at?.slice(0, 10)}</span>
            </div>
          )}
          {(data.payments?.mt5_orders || []).map((p, i) => (
            <div key={i} className="p-2.5 rounded-lg border border-white/5 bg-zinc-900/40 text-[11px] flex items-center justify-between">
              <span className="text-zinc-300">{p.account_type?.toUpperCase()} · {p.plan?.toUpperCase()} · ₹{p.amount_inr}</span>
              <span className={`font-mono text-[10px] ${p.status === "paid" ? "text-emerald-400" : "text-zinc-500"}`}>{p.status} · {p.paid_at?.slice(0, 10) || p.created_at?.slice(0, 10)}</span>
            </div>
          ))}
          {!data.payments?.signup_joining_fee && (data.payments?.mt5_orders || []).length === 0 && (
            <p className="text-[11px] text-zinc-500">No payment history.</p>
          )}
        </div>
      </Section>

      {/* Withdrawals */}
      <Section title="Withdrawals" icon={Wallet} testid="user-withdrawals">
        {(data.withdrawals || []).length === 0 && <p className="text-[11px] text-zinc-500">No withdrawals.</p>}
        <div className="space-y-2">
          {(data.withdrawals || []).slice(0, 20).map((w) => (
            <div key={w.withdrawal_id} className="p-2.5 rounded-lg border border-white/5 bg-zinc-900/40 text-[11px] flex items-center justify-between">
              <span className="text-zinc-300">₹{(w.amount || 0).toLocaleString()}</span>
              <span className={`font-mono text-[10px] ${
                w.status === "approved" ? "text-emerald-400" : w.status === "rejected" ? "text-rose-400" : "text-amber-400"
              }`}>{w.status} · {w.requested_at?.slice(0, 10)}</span>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, icon: Icon, children, testid }) {
  return (
    <div className="p-5 rounded-xl border border-white/10 bg-[#13131A]/60" data-testid={testid}>
      <h4 className="text-xs font-bold text-white mb-3 flex items-center gap-2">
        <Icon size={13} className="text-[#7DD3FC]" />
        {title}
      </h4>
      {children}
    </div>
  );
}

function StatTile({ label, value, icon: Icon, color }) {
  return (
    <div className="p-3 rounded-lg border border-white/10 bg-zinc-950/40">
      <div className="flex items-center gap-1.5 text-[9px] tracking-[0.2em] uppercase text-zinc-500 mb-1">
        <Icon size={10} />
        {label}
      </div>
      <div className={`text-lg font-bold ${color}`}>{value || "—"}</div>
    </div>
  );
}

function KV({ label, value, mono, col2 }) {
  return (
    <div className={col2 ? "md:col-span-2" : ""}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">{label}</div>
      <div className={`text-zinc-200 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    verified: "bg-emerald-500/10 text-emerald-400",
    active: "bg-emerald-500/10 text-emerald-400",
    active_unverified: "bg-sky-500/10 text-sky-300",
    paid_pending_approval: "bg-sky-500/10 text-sky-300",
    rejected: "bg-rose-500/10 text-rose-400",
    expired: "bg-zinc-600/20 text-zinc-400",
  };
  return (
    <span className={`px-2 py-0.5 rounded text-[9px] font-semibold uppercase ${map[status] || "bg-zinc-700/30 text-zinc-400"}`}>
      {(status || "—").replace(/_/g, " ")}
    </span>
  );
}

// ============================================================
// MT5 Verify / Reject inline buttons (any admin with mt5 perm)
// ============================================================
function MT5ActionButtons({ userId, accountType, account, onChanged }) {
  const [busy, setBusy] = useState("");
  const status = account?.status;
  const canVerify = status === "active_unverified" || status === "paid_pending_approval";
  const canReject = canVerify || status === "verified" || status === "active";
  if (!canVerify && !canReject) return null;

  const verify = async () => {
    setBusy("verify");
    try {
      await axios.post(`${API}/admin/mt5/activate/${userId}/${accountType}`, {}, { headers: getAuthHeaders() });
      toast.success(`${accountType.toUpperCase()} verified`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verify failed");
    } finally {
      setBusy("");
    }
  };

  const reject = async () => {
    const reason = window.prompt(`Reject reason for ${accountType.toUpperCase()} (will stop trading):`, "");
    if (reason === null) return;
    setBusy("reject");
    try {
      await axios.post(
        `${API}/admin/mt5/reject/${userId}/${accountType}`,
        { reason },
        { headers: getAuthHeaders() }
      );
      toast.success(`${accountType.toUpperCase()} rejected — trading stopped`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reject failed");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="flex gap-1.5 mt-2">
      {canVerify && (
        <button
          data-testid={`user-mt5-verify-${accountType}`}
          onClick={verify}
          disabled={!!busy}
          className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded text-[10px] font-semibold bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 disabled:opacity-40 transition-colors"
        >
          <CheckCircle2 size={10} /> {busy === "verify" ? "…" : "Verify"}
        </button>
      )}
      {canReject && (
        <button
          data-testid={`user-mt5-reject-${accountType}`}
          onClick={reject}
          disabled={!!busy}
          className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1 rounded text-[10px] font-semibold bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/30 text-rose-300 disabled:opacity-40 transition-colors"
        >
          <XCircle size={10} /> {busy === "reject" ? "…" : "Reject"}
        </button>
      )}
    </div>
  );
}

// ============================================================
// Master-only: income cap override + manual rank panel
// ============================================================
function AdminActionsPanel({ u, onChanged }) {
  const [busy, setBusy] = useState("");
  const [capPct, setCapPct] = useState(u.income_cap?.max_referral_bonus_pct || 50);
  const [capTeam, setCapTeam] = useState(!!u.income_cap?.team_pool_eligible);
  const [showCapForm, setShowCapForm] = useState(false);
  const [pickerTier, setPickerTier] = useState(u.rank?.tier || "affiliate");
  const [showRankForm, setShowRankForm] = useState(false);

  const liftCap = async () => {
    const reason = window.prompt("Reason for lifting the income cap:", "manual review approved");
    if (reason === null) return;
    setBusy("lift");
    try {
      await axios.put(`${API}/admin/users/${u.user_id}/income-cap`, { action: "lift", reason }, { headers: getAuthHeaders() });
      toast.success("Income cap lifted");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(""); }
  };

  const setCap = async () => {
    const reason = window.prompt(`Reason for setting cap to ${capPct}% (team pool ${capTeam ? "ON" : "OFF"}):`, "manual cap");
    if (reason === null) return;
    setBusy("set");
    try {
      await axios.put(
        `${API}/admin/users/${u.user_id}/income-cap`,
        { action: "set", max_referral_bonus_pct: capPct, team_pool_eligible: capTeam, reason },
        { headers: getAuthHeaders() }
      );
      toast.success(`Income cap set to ${capPct}%`);
      setShowCapForm(false);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(""); }
  };

  const setRank = async (tier) => {
    const isClear = tier === null;
    const action = isClear ? "Clear manual rank?" : `Pin rank to ${tier}?`;
    const reason = window.prompt(`${action}\nReason:`, isClear ? "auto-rank revert" : `manual ${tier}`);
    if (reason === null) return;
    setBusy("rank");
    try {
      await axios.put(
        `${API}/admin/users/${u.user_id}/rank-override`,
        { tier, reason },
        { headers: getAuthHeaders() }
      );
      toast.success(isClear ? "Rank override cleared" : `Pinned to ${tier}`);
      setShowRankForm(false);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBusy(""); }
  };

  return (
    <div className="p-4 rounded-xl border border-amber-500/20 bg-amber-500/5" data-testid="admin-actions-panel">
      <div className="flex items-center gap-2 mb-3">
        <ShieldOff size={12} className="text-amber-300" />
        <h4 className="text-[11px] font-bold text-amber-200 tracking-[0.15em] uppercase">Master Actions</h4>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Income cap controls */}
        <div className="p-3 rounded-lg bg-zinc-950/40 border border-white/5">
          <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">Income Cap</p>
          <p className="text-[11px] text-zinc-300 mb-2">
            {u.income_cap
              ? <>Currently <span className="text-rose-300 font-semibold">{u.income_cap.max_referral_bonus_pct}%</span> · team pool <span className="text-zinc-400">{u.income_cap.team_pool_eligible ? "ON" : "OFF"}</span></>
              : <span className="text-emerald-300">No cap — full 100% earnings</span>}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {u.income_cap && (
              <button
                data-testid="user-cap-lift"
                onClick={liftCap}
                disabled={!!busy}
                className="px-2 py-1 rounded text-[10px] font-semibold bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 disabled:opacity-40"
              >
                {busy === "lift" ? "…" : "Lift Cap"}
              </button>
            )}
            <button
              data-testid="user-cap-set-toggle"
              onClick={() => setShowCapForm(s => !s)}
              className="px-2 py-1 rounded text-[10px] font-semibold border border-white/10 text-zinc-300 hover:border-white/30"
            >
              {showCapForm ? "Cancel" : "Set Custom Cap"}
            </button>
          </div>
          {showCapForm && (
            <div className="mt-3 space-y-2" data-testid="user-cap-form">
              <div className="flex items-center gap-2">
                <input
                  data-testid="user-cap-pct"
                  type="number" min={1} max={100} value={capPct}
                  onChange={(e) => setCapPct(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                  className="w-20 px-2 py-1 rounded bg-zinc-950 border border-zinc-800 text-white text-xs font-mono"
                />
                <span className="text-[10px] text-zinc-500">% max referral bonus</span>
              </div>
              <label className="flex items-center gap-2 text-[10px] text-zinc-300">
                <input
                  data-testid="user-cap-team"
                  type="checkbox" checked={capTeam} onChange={(e) => setCapTeam(e.target.checked)}
                  className="rounded accent-[#7DD3FC]"
                /> Team pool eligible
              </label>
              <button
                data-testid="user-cap-save"
                onClick={setCap}
                disabled={!!busy}
                className="px-3 py-1 rounded text-[10px] font-semibold bg-[#7DD3FC] hover:bg-[#BAE6FD] text-[#0A0A0C] disabled:opacity-40"
              >
                {busy === "set" ? "Saving…" : "Apply Cap"}
              </button>
            </div>
          )}
        </div>

        {/* Manual rank controls */}
        <div className="p-3 rounded-lg bg-zinc-950/40 border border-white/5">
          <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-2">Manual Rank Override</p>
          <p className="text-[11px] text-zinc-300 mb-2">
            Current: <span className="text-white font-semibold">{u.rank?.label}</span>
            {u.rank?.manual && <span className="ml-1.5 text-amber-300">(pinned by {u.manual_rank?.set_by || "—"})</span>}
            <span className="text-zinc-500 ml-1.5">· {u.direct_referrals} actual referrals</span>
          </p>
          <div className="flex flex-wrap gap-1.5">
            {u.rank?.manual && (
              <button
                data-testid="user-rank-clear"
                onClick={() => setRank(null)}
                disabled={!!busy}
                className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold bg-zinc-600/20 hover:bg-zinc-600/30 border border-zinc-500/30 text-zinc-200 disabled:opacity-40"
              >
                Revert to Auto
              </button>
            )}
            <button
              data-testid="user-rank-toggle"
              onClick={() => setShowRankForm(s => !s)}
              className="px-2 py-1 rounded text-[10px] font-semibold border border-white/10 text-zinc-300 hover:border-white/30"
            >
              {showRankForm ? "Cancel" : "Promote / Demote"}
            </button>
          </div>
          {showRankForm && (
            <div className="mt-3 flex flex-wrap gap-1.5 items-center" data-testid="user-rank-form">
              <select
                data-testid="user-rank-tier"
                value={pickerTier}
                onChange={(e) => setPickerTier(e.target.value)}
                className="px-2 py-1 rounded bg-zinc-950 border border-zinc-800 text-white text-xs"
              >
                <option value="affiliate">Affiliate</option>
                <option value="sub_distributor">Sub-Distributor</option>
                <option value="main_distributor">Main Distributor</option>
              </select>
              {pickerTier === "main_distributor" || (pickerTier === "sub_distributor" && u.rank?.tier === "affiliate") ? (
                <button
                  data-testid="user-rank-promote"
                  onClick={() => setRank(pickerTier)}
                  disabled={!!busy}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 disabled:opacity-40"
                >
                  <ArrowUpCircle size={10} /> Promote
                </button>
              ) : (
                <button
                  data-testid="user-rank-demote"
                  onClick={() => setRank(pickerTier)}
                  disabled={!!busy}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-amber-300 disabled:opacity-40"
                >
                  <ArrowDownCircle size={10} /> Apply
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
